import React from 'react'
import { Assessment, Delete, Edit, ListAlt, Link as LinkIcon } from '@mui/icons-material'
import { IconButton } from '@mui/material'

import UserAvatar from '@tabeeb/modules/shared/userAvatar'

import { useDispatch, useSelector } from '@tabeeb/store'
import { fallbackUser } from '@tabeeb/modules/shared/users/common'
import { getContentId } from '@tabeeb/modules/shared/content/selectors'
import { getAIObjects } from '@tabeeb/modules/artificialIntelligence/selectors'
import { getHasAIReportAccess } from '@tabeeb/modules/aiReport/selectors'

import {
  getUniqueAiObjectForEditRequest,
  getUniqueAiObjects,
  openEditUniqueAiObjectConnectionsDialog,
  setSelectedAiObject,
  setUniqueAiObjectAnnotationForEdit,
} from '@tabeeb/modules/artificialIntelligence/actions'
import { AnnotationType, type AnnotationResponse } from '@tabeeb/contracts/main-api/swagger-main-api'
import { AIObjectResponse } from '@tabeeb/contracts/microservices/swagger-ai-service'
import { openAiReportDialog, selectAiObject } from '@tabeeb/modules/aiReport/actions'
import { useAIAnnotationText } from '@tabeeb/modules/artificialIntelligence/hooks'
import AnnotationText from '../AnnotationText/AnnotationText'
import { closeHotspotDialog, deleteContentAnnotations } from '../../actions'
import { useUserById } from '../../../../users/hooks'
import { useAnnotationById } from '../../hooks'
import { Name, Time, Content, Container, Header, Actions } from './Message.styles'

import './rhap.less'

function convertDateToString(dateTime: string): string {
  const date = new Date(dateTime)
  const now = new Date()
  if (now.toDateString() === date.toDateString()) {
    return `${date.toLocaleTimeString(navigator.language, {
      hour: '2-digit',
      minute: '2-digit',
    })}, today`
  }
  return date.toLocaleDateString()
}

interface MessageProps {
  id: number
  onEdit: (annotation: AnnotationResponse) => void
}

const Message: React.FC<MessageProps> = ({ id, onEdit }) => {
  const dispatch = useDispatch()

  const annotation = useAnnotationById(id)
  const user = useUserById(annotation?.UserId) || fallbackUser
  const contentId: number = useSelector(getContentId)
  const aiObjects: AIObjectResponse[] = useSelector(getAIObjects)
  const hasAIReportAccess: boolean = useSelector(getHasAIReportAccess)

  const { loading, aiAnnotationText } = useAIAnnotationText({
    aiAnnotationId: id,
    enabled: annotation?.Type === AnnotationType.AI,
  })

  const canEditProperties = Boolean(aiAnnotationText?.UniqueAIObjectId)
  const canSeeReport = hasAIReportAccess && canEditProperties

  const handleDelete = () => {
    if (!annotation?.Id) {
      return
    }

    dispatch(deleteContentAnnotations({ annotationIds: [annotation.Id] }))
  }

  const handleEditConnections = () => {
    dispatch(closeHotspotDialog())

    if (!aiAnnotationText?.UniqueAIObjectId) {
      return
    }

    dispatch(openEditUniqueAiObjectConnectionsDialog(aiAnnotationText.UniqueAIObjectId))
  }

  const handleEditAIObjectProperties = () => {
    dispatch(closeHotspotDialog())

    if (!annotation) {
      return
    }

    dispatch(setUniqueAiObjectAnnotationForEdit(annotation))

    if (!aiAnnotationText?.UniqueAIObjectId) {
      return
    }

    dispatch(getUniqueAiObjectForEditRequest({ uniqueAIObjectId: aiAnnotationText.UniqueAIObjectId }))
  }

  const handleEditAIObject = () => {
    if (!aiAnnotationText?.AIObjectId) {
      return
    }

    const selectedAIObject = aiObjects.find((object) => object.Id === aiAnnotationText.AIObjectId) ?? aiObjects[0]
    if (selectedAIObject) {
      dispatch(setSelectedAiObject(selectedAIObject))
      dispatch(getUniqueAiObjects.request({ aiObjectId: selectedAIObject.Id, contentId }))
    }

    if (annotation) {
      onEdit(annotation)
    }
  }

  const handleOpenAIObjectReport = () => {
    dispatch(closeHotspotDialog())
    dispatch(openAiReportDialog())

    if (!aiAnnotationText?.AIObjectId) {
      return
    }

    dispatch(
      selectAiObject({ aiObjectId: aiAnnotationText.AIObjectId, uniqueAiObjectId: aiAnnotationText.UniqueAIObjectId })
    )
  }

  return (
    <Container>
      <UserAvatar style={{ marginRight: 15 }} user={user} />
      <Content>
        <Header>
          <Name>{user.displayName}</Name>
          <Actions>
            {canSeeReport && (
              <IconButton title='Show report' size='small' onClick={handleOpenAIObjectReport}>
                <Assessment fontSize='small' />
              </IconButton>
            )}
            {canEditProperties && (
              <IconButton title='Edit properties' size='small' onClick={handleEditAIObjectProperties}>
                <ListAlt fontSize='small' />
              </IconButton>
            )}
            {canEditProperties && (
              <IconButton title='Edit connections' size='small' onClick={handleEditConnections}>
                <LinkIcon fontSize='small' />
              </IconButton>
            )}
            {aiAnnotationText?.AIObjectId && (
              <IconButton title='Edit AI object' size='small' onClick={handleEditAIObject}>
                <Edit fontSize='small' />
              </IconButton>
            )}
            <IconButton title='Delete message' size='small' onClick={handleDelete}>
              <Delete fontSize='small' />
            </IconButton>
          </Actions>
        </Header>
        <AnnotationText annotation={annotation} aiAnnotationText={aiAnnotationText} loading={loading} />
        {annotation?.CreatedOn && <Time>Posted on {convertDateToString(annotation.CreatedOn)}</Time>}
      </Content>
    </Container>
  )
}

export default Message
